import React from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ScheduleIcon from '@mui/icons-material/Schedule'
import BlockIcon from '@mui/icons-material/Block'
import { QRCodeSVG } from 'qrcode.react'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { buildQrPayload } from '../../../../../../../generic/qr/qrPayload'
import { BLOCK_STATUS } from '../../../hooks/useJobDetail'
import { shortSerial } from '../../../utils/jobDetailFormat'
import { BlockState, DetailSection, Pill } from '../JobDetailPrimitives'
import PartFootprint from '../footprint/PartFootprint'

// Historia 160 (CU8, RN37-RN42, D9/D24/D27, mock 6) — PIEZAS ASOCIADAS.
//
//   · INSTANCIAS DE <referencia>: TODAS las piezas de la OF, sin límite ni paginación, con su propio
//     desplazamiento (S100); nº de serie corto (RN38) e icono de estado en ESTE job (RN37). Las
//     canceladas, atenuadas y fuera de los totales (D9). Chips: terminadas, no conformes, pendientes
//     (las mismas cifras que CANTIDADES, INV3); no hay chip de "en curso" (D19).
//   · PIEZA SELECCIONADA: nº completo, chip de estado y su QR con el componente de MIC (D15).
//   · HUELLA DIGITAL EN ESTE JOB (RN41). Un 404 de la huella se queda en su panel (S99).

export const PART_STATE = {
  FINISHED: { labelKey: 'Finished part', tone: 'success', Icon: CheckIcon },
  NON_CONFORMING: { labelKey: 'Non-conforming part', tone: 'error', Icon: WarningAmberRoundedIcon },
  IN_PROGRESS: { labelKey: 'Part in progress', tone: 'info', Icon: PlayArrowIcon },
  PENDING: { labelKey: 'Pending part', tone: 'default', Icon: ScheduleIcon },
  CANCELLED: { labelKey: 'Cancelled part', tone: 'default', Icon: BlockIcon }
}

const stateOf = (state) => PART_STATE[state] || PART_STATE.PENDING

const CELL_BG = {
  success: 'rgba(127, 225, 167, 0.14)',
  error: 'rgba(255, 99, 99, 0.18)',
  info: 'rgba(66, 165, 245, 0.2)',
  default: 'action.hover'
}
const CELL_FG = { success: 'primary.main', error: 'error.light', info: 'info.light', default: 'text.secondary' }

/** D15: la carga útil MIC-QR de la pieza (identidad + código). */
export const partQrPayload = (part = {}) => buildQrPayload({
  entityType: 'Part',
  id: part.id,
  entries: [{ field: 'code', value: part.code || '' }]
})

const PartCell = React.memo(({ part, referenceCode, selected, onSelect }) => {
  const { t_ } = useSmartTranslation()
  const state = stateOf(part.state)
  const cancelled = part.state === 'CANCELLED'
  return (
    <ButtonBase
      onClick={() => onSelect(part.id)}
      aria-pressed={selected}
      aria-label={part.code + ' · ' + t_(state.labelKey)}
      title={t_(state.labelKey)}
      data-testid={'part-cell-' + part.id}
      data-state={part.state}
      sx={{
        gap: 0.5,
        px: 1.25,
        height: 38,
        minWidth: 88,
        borderRadius: 1.5,
        fontFamily: 'monospace',
        fontWeight: 700,
        fontSize: '0.95rem',
        bgcolor: CELL_BG[state.tone],
        color: CELL_FG[state.tone],
        opacity: cancelled ? 0.45 : 1,
        border: 2,
        borderColor: selected ? 'currentColor' : 'transparent'
      }}
    >
      <state.Icon sx={{ fontSize: 16 }} />
      {shortSerial(part.code, referenceCode)}
    </ButtonBase>
  )
})

PartCell.displayName = 'PartCell'
PartCell.propTypes = { part: PropTypes.object, referenceCode: PropTypes.string, selected: PropTypes.bool, onSelect: PropTypes.func }

const PartsTab = ({
  block = {},
  selectedPartId = '',
  footprint = {},
  fabricationOrderId = '',
  jobReferenceId = '',
  onSelectPart = () => {},
  onRetry = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const data = block.data || {}
  const parts = data.parts || []
  const totals = data.totals || {}
  const referenceCode = data.partReferenceCode || ''
  const selectedPart = parts.find((part) => part.id === selectedPartId)
  const footprintData = footprint.partId === selectedPartId ? (footprint.data || {}) : {}
  const footprintReady = footprint.status === BLOCK_STATUS.READY && footprint.partId === selectedPartId
  const footprintFailed = footprint.status === BLOCK_STATUS.FAILED && footprint.partId === selectedPartId

  const renderSelected = () => {
    if (!selectedPart) {
      return <Typography color='text.secondary' sx={{ p: 2 }} data-testid='select-a-part'>{t_('Select a part')}</Typography>
    }
    const state = stateOf(selectedPart.state)
    return (
      <Stack alignItems='center' spacing={1.5} sx={{ p: 2 }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.2rem' }} data-testid='selected-part-code'>{selectedPart.code}</Typography>
        <Pill label={t_(state.labelKey)} tone={state.tone} icon={<state.Icon sx={{ fontSize: 14 }} />} testId='selected-part-state' />
        <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 2, lineHeight: 0 }}>
          <QRCodeSVG value={partQrPayload(selectedPart)} size={168} level='Q' data-testid='part-qr' data-payload={partQrPayload(selectedPart)} />
        </Box>
      </Stack>
    )
  }

  const renderFootprint = () => {
    if (!selectedPart) {
      return null
    }
    if (footprintFailed) {
      return (
        <Typography color='error' sx={{ p: 2 }} role='alert' data-testid='footprint-error'>
          {t_(footprint.errorKey || 'This block could not be loaded')}
        </Typography>
      )
    }
    if (!footprintReady) {
      return <Stack alignItems='center' sx={{ p: 2 }}><CircularProgress size={28} aria-label={t_('Loading')} /></Stack>
    }
    return (
      <PartFootprint
        data={footprintData}
        fabricationOrderId={fabricationOrderId}
        jobReferenceId={jobReferenceId}
        partId={selectedPartId}
      />
    )
  }

  return (
    <BlockState status={block.status} onRetry={onRetry}>
      <Stack spacing={2}>
        <Stack direction='row' spacing={2} alignItems='stretch'>
          <DetailSection
            title={t_('Instances of {{reference}}', { reference: referenceCode })}
            testId='parts-grid-block'
            sx={{ flex: '1 1 auto', minWidth: 0 }}
            action={(
              <Stack direction='row' spacing={1} data-testid='parts-totals'>
                <Pill label={t_('{{count}} finished', { count: totals.finished || 0 })} tone='success' icon={<CheckIcon sx={{ fontSize: 14 }} />} testId='parts-total-finished' />
                <Pill label={t_('{{count}} non-conforming', { count: totals.nonConforming || 0 })} tone='error' icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />} testId='parts-total-non-conforming' />
                <Pill label={t_('{{count}} pending parts', { count: totals.pending || 0 })} icon={<ScheduleIcon sx={{ fontSize: 14 }} />} testId='parts-total-pending' />
              </Stack>
            )}
          >
            <Box
              data-testid='parts-grid'
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, alignContent: 'flex-start', maxHeight: 300, overflowY: 'auto' }}
            >
              {parts.map((part) => (
                <PartCell key={part.id} part={part} referenceCode={referenceCode} selected={part.id === selectedPartId} onSelect={onSelectPart} />
              ))}
            </Box>
          </DetailSection>
          <DetailSection title={t_('Selected part')} testId='selected-part' sx={{ flex: '0 0 340px', borderColor: 'primary.main' }}>
            {renderSelected()}
          </DetailSection>
        </Stack>
        <DetailSection title={t_('Digital footprint in this job')} testId='part-footprint'>
          {renderFootprint()}
        </DetailSection>
      </Stack>
    </BlockState>
  )
}

PartsTab.propTypes = {
  block: PropTypes.object,
  selectedPartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  footprint: PropTypes.object,
  fabricationOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobReferenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectPart: PropTypes.func,
  onRetry: PropTypes.func
}

export default React.memo(PartsTab)
